import { paths } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { upperCase } from 'lodash';
import { useOrganizationsCsvDownloadQuery } from '@common/cache';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useOrganizationTableStore,
  PersistedOrganizationColumn,
  initialOrganizationColumns,
} from '../Filters/filters.store';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type IOrganizationTableEntry =
  paths['/record/organizations']['get']['responses']['200']['content']['application/json']['records'][number];

export type { IOrganizationTableFilters } from '../Filters/filters.store';

const buildOrganizationColumns = (): GridColDef<IOrganizationTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {params.row.image ? (
          <img
            alt='organization thumbnail'
            style={{ width: 24, height: 24, marginRight: 32 }}
            src={params.row.image.thumbnail_xsmall}
          />
        ) : (
          <ApartmentIcon
            aria-label='Default organization icon'
            color='primary'
            sx={{ width: 24, height: 24, marginRight: 4, flexShrink: 0 }}
          />
        )}
        <TruncatedTextWithConditionalTooltip
          width='100%'
          variant='body2'
          text={params.row.name}
        />
      </Box>
    ),
  },
  {
    field: 'user_count',
    headerName: 'Users',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.user_count}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'partner_count',
    headerName: 'Partners',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.partner_count}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'admin_count',
    headerName: 'Admins',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.admin_count}
        variant='body2'
        width='100%'
      />
    ),
  },
];

export const useOrganizationTable = () => {
  const {
    filters,
    setFilters,
    clearStore,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useOrganizationTableStore();
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const { data, isPending, mutateAsync } = useOrganizationsCsvDownloadQuery();

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organizations`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => setFilters({ search: '' });
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Organizations Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.search !== '' && { search: filters.search }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ sort_by: 'admin_count', sort_order: 'asc' });
      return;
    }
    setFilters({ sort_by: field as typeof sort_by, sort_order: sort });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ limit: model.pageSize, page: model.page + 1 });
  };

  const handleSearchChange = (event: { target: { value: string } }) => {
    setSearchInputValue(event.target.value);
    setFilters({ search: event.target.value });
  };

  const runtimeColumns = useMemo(() => buildOrganizationColumns(), []);
  const columns = buildGridColumns<
    IOrganizationTableEntry,
    PersistedOrganizationColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IOrganizationTableEntry>,
  ): PersistedOrganizationColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const MIN_VISIBLE_COLUMNS = 4;

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    const allFields = columns.map(col => col.field);
    const nextHidden = allFields.filter(field => model[field] === false);
    const visibleCount = allFields.length - nextHidden.length;
    if (visibleCount < MIN_VISIBLE_COLUMNS) return;
    setHiddenColumns(nextHidden);
  };

  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;

  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleResetAll = () => {
    clearStore();
    setSearchInputValue('');
    resetView();
  };

  return {
    filters,
    columns: columnsWithHideable,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    sort_by,
    sort_order,
    handleDownload,
    isDownloading: isPending,
    searchInputValue,
    handleResetAll,
    initialSortFilters: { sort_order: 'asc', sort_by: 'name' },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    initialColumns: initialOrganizationColumns,
    apiRef,
  };
};
